import { useEffect, useState } from 'react';
import { IconClose, IconCheck, IconRefresh } from './icons';

interface DiffEntry {
  key: string;
  label: string;
  before: unknown;
  after: unknown;
}

interface Props {
  open: boolean;
  title: string;
  diffs: DiffEntry[];
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
  rememberSkipKey?: string;
}

const SKIP_KEY_PREFIX = 'admin.diff.skip.v1.';

/**
 * Centered modal that lists every changed field with before -> after, then
 * asks for explicit confirmation before persisting. The user can opt out
 * with "Don't show again" (per-context, scoped by `rememberSkipKey`).
 *
 * Returns null when not open; consumers should also short-circuit when the
 * skip flag is set in localStorage (helper: `shouldSkipDiff`).
 */
export function DiffModal({ open, title, diffs, onCancel, onConfirm, rememberSkipKey }: Props) {
  const [confirming, setConfirming] = useState(false);
  const [skip, setSkip] = useState(false);

  // Reset confirming state every time the modal opens.
  useEffect(() => {
    if (open) {
      setConfirming(false);
      setSkip(false);
    }
  }, [open]);

  // Esc to cancel
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onCancel(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  async function confirm() {
    if (confirming) return;
    if (skip && rememberSkipKey) {
      try { localStorage.setItem(SKIP_KEY_PREFIX + rememberSkipKey, '1'); } catch { /* ignore */ }
    }
    setConfirming(true);
    try { await onConfirm(); }
    finally { setConfirming(false); }
  }

  return (
    <div className="fixed inset-0 z-[55] bg-navy/30 backdrop-blur-[2px] grid place-items-center px-4" onClick={onCancel}>
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-[640px] max-h-[80vh] bg-white rounded-xl shadow-pop border border-line flex flex-col"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-line-soft">
          <div>
            <h2 className="h-display text-[16px]">{title}</h2>
            <p className="text-[12px] text-ink-muted mt-0.5">{diffs.length} field{diffs.length !== 1 ? 's' : ''} changing</p>
          </div>
          <button onClick={onCancel} className="h-8 w-8 grid place-items-center rounded-md hover:bg-cream-tint">
            <IconClose className="w-4 h-4 text-ink-muted" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto scroll-area px-5 py-3 divide-y divide-line-soft">
          {diffs.map(d => (
            <div key={d.key} className="py-3 grid grid-cols-[120px_1fr] gap-3 text-[13px]">
              <div className="text-[11px] uppercase tracking-wider text-ink-muted pt-1">{d.label}</div>
              <div className="space-y-1.5">
                <div className="flex items-baseline gap-2">
                  <span className="text-[10.5px] uppercase text-ink-faint w-12 shrink-0">Before</span>
                  <span className="text-ink-muted font-mono text-[12px] line-through decoration-red-300/60 break-words">{formatValue(d.before)}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-[10.5px] uppercase text-funds-deep font-medium w-12 shrink-0">After</span>
                  <span className="text-ink font-mono text-[12px] break-words">{formatValue(d.after)}</span>
                </div>
              </div>
            </div>
          ))}
          {diffs.length === 0 && (
            <div className="py-8 text-center text-ink-muted text-[13px]">No changes detected.</div>
          )}
        </div>

        <footer className="px-5 py-3.5 border-t border-line-soft flex items-center justify-between">
          {rememberSkipKey ? (
            <label className="text-[12.5px] text-ink-muted flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={skip}
                onChange={e => setSkip(e.target.checked)}
                className="w-4 h-4 rounded border-line text-navy focus:ring-navy/20"
              />
              Don't show again
            </label>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onCancel} className="h-9 px-3 text-[13px] rounded-md border border-line bg-white hover:bg-cream-tint">Cancel</button>
            <button
              onClick={confirm}
              disabled={confirming || diffs.length === 0}
              className="h-9 px-3.5 text-[13px] rounded-md bg-navy text-white hover:bg-navy-soft font-medium flex items-center gap-1.5 disabled:opacity-50"
            >
              {confirming ? <IconRefresh className="w-4 h-4 animate-spin" /> : <IconCheck className="w-4 h-4" />}
              {confirming ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

export function shouldSkipDiff(key: string): boolean {
  try { return localStorage.getItem(SKIP_KEY_PREFIX + key) === '1'; }
  catch { return false; }
}

export function clearSkipDiff(key: string) {
  try { localStorage.removeItem(SKIP_KEY_PREFIX + key); } catch { /* ignore */ }
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '(empty)';
  if (typeof v === 'string') return v.length > 200 ? v.slice(0, 200) + '…' : (v || '(empty)');
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.length === 0 ? '(empty)' : v.join(', ');
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}
